import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useModelOptions } from '@/lib/useModelOptions';

/**
 * The per-provider model dropdown, shared by the agent detail page and the workflow
 * builder's LLM node inspector.
 *
 * Lifted out of AgentDetail.jsx when the workflow builder needed the same control: the
 * builder previously took a model as free text, so a typo only surfaced as a provider error
 * mid-run. Two copies of the list would have drifted the first time a model shipped.
 *
 * The list itself is no longer hardcoded — it comes from the server's model catalogue for
 * this exact (provider, connection) pair, so a model that launched this morning appears
 * without a code change. `connectionId` matters because two connections for the same
 * provider are two different accounts, and an account can be entitled to models another
 * one cannot see.
 */

const CUSTOM_OPTION = '__custom__';

const controlClass = 'border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]';

/** A per-provider model dropdown with a "Custom…" fallback to free text — never dead
 *  ends on a model name the catalogue doesn't happen to know about. */
export default function ModelSelect({
  label, value, onChange, provider, connectionId = null,
}) {
  const { models, loading, refresh } = useModelOptions({ provider, connectionId });
  const known = models.map((m) => m.id);

  // null = decide from the data, 'custom'/'list' = the user overrode that decision.
  // The auto branch waits for `loading` to clear: judging an unrecognised value against
  // a list still in flight would flip a perfectly valid saved model into free-text mode
  // for a frame, every time the screen opens.
  const [mode, setMode] = useState(null);
  useEffect(() => { setMode(null); }, [provider, connectionId]);
  const customMode = mode === 'custom' || (mode === null && !loading && !!value && !known.includes(value));

  if (customMode) {
    return (
      <div>
        {label && <label className="block text-sm mb-1">{label}</label>}
        <div className="flex gap-2">
          <input
            className={`${controlClass} flex-1`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Model name"
          />
          {known.length > 0 && (
            <button type="button" onClick={() => setMode('list')} className="text-xs px-2 rounded border border-[var(--app-border)]">List</button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div>
      {label && <label className="block text-sm mb-1">{label}</label>}
      <div className="flex gap-2 items-center">
        <select
          className={`${controlClass} flex-1`}
          value={known.includes(value) ? value : ''}
          onChange={(e) => (e.target.value === CUSTOM_OPTION ? setMode('custom') : onChange(e.target.value))}
        >
          <option value="" disabled>{loading ? 'Loading models…' : 'Select a model…'}</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>{m.label && m.label !== m.id ? `${m.id} — ${m.label}` : m.id}</option>
          ))}
          <option value={CUSTOM_OPTION}>Custom…</option>
        </select>
        <button
          type="button"
          onClick={refresh}
          title="Refresh model list from the provider"
          className="text-secondary-500 hover:text-secondary-700 p-1"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
    </div>
  );
}
